import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input
} from "@fluentui/react-components";
import { Link20Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";

type InsertLinkDialogProps = {
  onClose: () => void;
  onInsert: (url: string) => void;
};

export function InsertLinkDialog({
  onClose,
  onInsert
}: InsertLinkDialogProps): React.JSX.Element {
  const [url, setUrl] = useState("https://");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isValidUrl = useCallback((value: string) => {
    if (!value || value === "https://" || value === "http://") {
      return false;
    }

    // Check if value is relative URL
    const isRelative = !/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value);

    if (isRelative) {
      // Reject relative URLs with spaces or suspicious characters
      return !value.includes(" ");
    }

    // Try parsing absolute URL
    try {
      const tempUrl = new URL(value);

      // Only allow safe protocols
      const safeProtocols = ["http:", "https:", "mailto:", "tel:"];
      return safeProtocols.includes(tempUrl.protocol);
    } catch {
      return false; // Invalid URL
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (isValidUrl(url)) {
      onInsert(url);
      onClose();
    }
  }, [url, isValidUrl, onInsert, onClose]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleConfirm();
    },
    [handleConfirm]
  );

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, []);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <Dialog open onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className="link-dialog-surface">
        <form onSubmit={handleSubmit}>
          <DialogBody>
            <DialogTitle className="link-dialog-title">
              <Link20Regular className="link-dialog-icon" />
              Infoga länk
            </DialogTitle>
            <DialogContent className="link-dialog-content">
              <Field label="URL" size="small">
                <Input
                  ref={inputRef}
                  size="small"
                  value={url}
                  onChange={(_, data) => setUrl(data.value)}
                  placeholder="https://example.com"
                  type="url"
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={onClose} type="button">
                Avbryt
              </Button>
              <Button
                appearance="primary"
                type="submit"
                disabled={!isValidUrl(url)}
              >
                Infoga länk
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}
